import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, type SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem as MuiMenuItem,
  Select as MuiSelect,
  styled,
} from '@mui/material';
import { UserPlus } from 'lucide-react';
import * as z from 'zod';

import Button from '../../common/Button';
import EmailInputChips from '../../common/EmailInputChips';
import { extractValue } from '../../common/utils';
import {
  useCreateOrgInvitation,
  useGetOrganizationById,
  useGetOrgMembers,
} from '../../hooks/tanstack/useOrganizationHook';
import { useGetRouteParams } from '../../hooks/tanstack/useUtilsHook';
import { type OrganizationRole, type Role } from '../../types';
import { handleQueryError } from '../../utils/api';
import cn from '../../utils/cn';
import SuccessDialog from '../SuccessDialog';

const Select = styled(MuiSelect)({
  fontSize: '14px',
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none !important',
  },

  '& .MuiSelect-select': {},
  '& .MuiSelect-nativeInput': {},
});

const EmailSchema = z.string().trim().email('Invalid email address');

type EmailSchemaType = z.infer<typeof EmailSchema>;

const MenuItem = styled(MuiMenuItem)({
  fontSize: '14px',
});

type InviteFormDialogProps = {
  open: boolean;
  handleClose: () => void;
  handleOpen: () => void;
  roles: {
    label: string;
    value: OrganizationRole;
  }[];
  to?: 'organization' | 'project';
  showTrigger?: boolean;
  iUser?: {
    id: number;
    email: string;
    fullName: string;
  };
  trigger?: {
    label: React.ReactNode;
    style?: React.CSSProperties;
  };
};

const InviteOrgFormDialog = ({
  open,
  handleClose,
  handleOpen,
  roles,
  trigger,
  iUser,
  showTrigger = true,
}: InviteFormDialogProps) => {
  const inputId = 'emails';

  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [error, setError] = useState<Record<string, string>>({});

  const [emails, setEmails] = useState<{ value: string; isError?: boolean }[]>(
    []
  );

  const ref = useRef<HTMLInputElement>(null);

  const form = useForm();

  const {
    formState,
    setError: setFormError,
    reset,
    register,
    watch,
    clearErrors,
  } = form;

  const role = watch('role') as Role;

  const { isSubmitting, errors } = formState;

  const { orgId } = useGetRouteParams();

  useEffect(() => {
    if (error) {
      Object.keys(error).forEach((key) => {
        setFormError(key, {
          type: 'custom',
          message: error[key],
        });
      });
    }
  }, [error]);

  const { data: currentProject } = useGetOrganizationById(
    {
      orgId,
    },
    {
      enabled: !!orgId,
    }
  );

  const { data: orgMembers } = useGetOrgMembers(
    {
      orgId,
    },
    {
      enabled: !!orgId,
    }
  );

  const { mutateAsync: createOrgInvitation, isPending: isSendingInvitation } =
    useCreateOrgInvitation({
      onSuccess() {
        handleCloseDialog();
        reset();
        handleOpenSuccessDialog();
      },
      onError(error) {
        clearErrors();
        handleQueryError(error, setError);
      },
    });

  useEffect(() => {
    reset({ role: roles[0].value });
  }, []);

  const handleOpenSuccessDialog = () => {
    setOpenSuccessDialog(true);
  };

  const handleCloseSuccessDialog = () => {
    setOpenSuccessDialog(false);
  };

  const handleCloseDialog = () => {
    reset();
    setError({});
    clearErrors();
    setEmails([]);
    handleClose();
  };

  const handleError = ({
    message,
    email,
    ref,
  }: {
    email: string;
    message: string;
    ref: React.RefObject<HTMLInputElement>;
  }) => {
    setFormError(`${inputId}.${email.replaceAll('.', '')}`, { message });
    setEmails((prev) => [
      ...prev,
      {
        value: email,
        isError: true,
      },
    ]);
    if (ref.current) {
      ref.current.value = '';
    }
  };

  const handleAddEmail = (email: EmailSchemaType) => {
    const { error } = EmailSchema.safeParse(email);

    if (error) {
      handleError({
        email,
        message: error.errors[0].message,
        ref,
      });
      return;
    }

    if (orgMembers?.find((member) => member.user.email === email)) {
      handleError({
        email,
        message: 'This user already exists in the organization',
        ref,
      });
      return;
    }

    setEmails((prev) => [...prev, { value: email }]);

    if (ref.current) {
      ref.current.value = '';
    }
  };

  const handleRemoveEmail = (email: string) => {
    clearErrors(`${inputId}.${email.replaceAll('.', '')}`);
    setEmails((prev) => prev.filter((e) => e.value !== email));
  };

  const onSubmit: SubmitHandler<any> = async () => {
    const currentEmail = ref.current?.value;

    if (!emails.length && !currentEmail) {
      setFormError(inputId, {
        message: 'You must enter at least one email',
      });
      setError({});
      return;
    }

    if (!role) {
      setFormError(inputId, { message: 'Please select a role' });
      setError({});
      return;
    }

    if (!emails.length && currentEmail) {
      if (orgMembers?.find((member) => member.user.email === currentEmail)) {
        setFormError(inputId, {
          message: 'This user already exists in the organization',
        });
        setError({});
        return;
      }

      createOrgInvitation({
        orgId,
        data: {
          email: currentEmail,
          role,
        },
      });
      return;
    }

    await Promise.all(
      emails.map((email) =>
        createOrgInvitation({
          orgId,
          data: {
            email: email.value,
            role,
          },
        })
      )
    );
  };

  const errorsKeys = Object.keys(errors[inputId] ?? {});

  const errorMessage = useMemo(
    () =>
      errors[inputId]
        ? ((errors[inputId].message as string) ??
          (errors[inputId] as Record<string, { message: string }>)[
            errorsKeys[errorsKeys.length - 1]
          ]?.message)
        : extractValue(error),
    [errorsKeys, error]
  );

  return (
    <>
      {showTrigger && (
        <button
          type="button"
          onClick={handleOpen}
          style={{
            color: 'white',
            backgroundColor: '#B8341B',
            display: 'flex',
            gap: 4,
            borderRadius: 4,
            padding: '6px 12px',
            ...trigger?.style,
          }}
        >
          {trigger?.label ?? (
            <div className="flex w-fit items-center gap-2">
              <UserPlus size={20} />
              <p className="w-fit">Invite users</p>
            </div>
          )}
        </button>
      )}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle className="w-full">
          <h2 className="pt-2 text-2xl font-bold">
            Invite user(s) to {currentProject?.name}
          </h2>
        </DialogTitle>
        <DialogContent>
          <div>
            <FormProvider {...form}>
              <form className="mb-2 space-y-6">
                <div className="flex flex-col gap-2">
                  <div
                    className={cn(
                      'flex items-center justify-between rounded-[4px] border-2 border-[#2196F3] px-2',
                      {
                        'border-primary-red': errorMessage,
                      }
                    )}
                  >
                    <div className="flex w-full flex-wrap items-center gap-1 py-2">
                      <EmailInputChips
                        emails={emails}
                        onRemoveEmail={handleRemoveEmail}
                      />
                      <input
                        ref={ref}
                        name="email"
                        placeholder='Type email and press "Enter" or ","'
                        value={iUser?.email}
                        disabled={!!iUser}
                        onKeyDown={(e) => {
                          const keyCode = e.key;

                          const isEmpty =
                            e.currentTarget.value.replace(',', '').length === 0;

                          if (isEmpty && ref.current) {
                            ref.current.value = '';
                            return;
                          }

                          if (keyCode === 'Enter' || keyCode === ',') {
                            e.preventDefault();
                            handleAddEmail(
                              e.currentTarget.value.replace(',', '')
                            );
                          }
                        }}
                        className="grow outline-none"
                      />
                    </div>
                    <Select
                      {...register('role')}
                      defaultValue={roles[0].value}
                      size="small"
                      className="bottom-0"
                      placeholder="Please select"
                    >
                      {roles
                        .filter((r) => r.value !== 'organization_owner')
                        .map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            <span className="capitalize">{option.label}</span>
                          </MenuItem>
                        ))}
                    </Select>
                  </div>
                  {errorMessage && (
                    <p className="px-2 text-sm text-primary-red">
                      {errorMessage}
                    </p>
                  )}
                </div>
                <div className="flex-end flex justify-end gap-3">
                  <Button
                    onClick={handleCloseDialog}
                    variant="outlined"
                    disabled={isSubmitting || isSendingInvitation}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={onSubmit}
                    loading={isSubmitting || isSendingInvitation}
                    disabled={isSubmitting || isSendingInvitation}
                    variant="contained_blue"
                  >
                    Send
                  </Button>
                </div>
              </form>
            </FormProvider>
          </div>
        </DialogContent>
      </Dialog>
      <SuccessDialog
        content="Invitation sent successfully."
        open={openSuccessDialog}
        handleOnClose={handleCloseSuccessDialog}
      />
    </>
  );
};

export default InviteOrgFormDialog;
